import { v4 as uuid4 } from "uuid"
import {
  DecisionReason,
  EventType,
  Experiment,
  HackleEvent,
  HackleUser,
  IdentifierType,
  Long,
  Properties,
  RemoteConfigParameter,
  VariationId,
  VariationKey
} from "../model/model"
import { EventDto, ExposureEventDto, RemoteConfigEventDto, TrackEventDto } from "./dto"
import PropertyUtil from "../util/PropertyUtil"
import ExperimentEvaluation from "../evaluation/evalautor/experiment/ExperimentEvaluation"
import RemoteConfigEvaluation from "../evaluation/evalautor/remoteconfig/RemoteConfigEvaluation"

export default abstract class UserEvent {
  timestamp: Long
  user: HackleUser
  public readonly insertId: string

  protected constructor(timestamp: Long, user: HackleUser, insertId: string = uuid4()) {
    this.timestamp = timestamp
    this.user = user
    this.insertId = insertId
  }

  static exposure(
    user: HackleUser,
    evaluation: ExperimentEvaluation,
    properties: Properties,
    timestamp: number
  ): ExposureEvent {
    return new ExposureEvent(
      timestamp,
      user,
      evaluation.experiment,
      evaluation.variationId,
      evaluation.variationKey,
      evaluation.reason,
      properties
    )
  }

  static track(eventType: EventType, event: HackleEvent, user: HackleUser, timestamp: number) {
    return new TrackEvent(timestamp, user, eventType, event)
  }

  static remoteConfig(
    user: HackleUser,
    evaluation: RemoteConfigEvaluation,
    properties: Properties,
    timestamp: number
  ): RemoteConfigEvent {
    return new RemoteConfigEvent(
      timestamp,
      user,
      evaluation.parameter,
      evaluation.valueId,
      evaluation.reason,
      properties
    )
  }

  static isExposureEvent(event: UserEvent): event is ExposureEvent {
    return (event as ExposureEvent).experiment !== undefined
  }

  static isTrackEvent(event: UserEvent): event is TrackEvent {
    return (event as TrackEvent).eventType !== undefined
  }

  static isRemoteConfigEvent(event: UserEvent): event is RemoteConfigEvent {
    return (event as RemoteConfigEvent).parameter !== undefined
  }

  static isExposureEventDto(event: EventDto): event is ExposureEventDto {
    return (
      "experimentId" in (event as ExposureEventDto) && typeof (event as ExposureEventDto).experimentId !== "undefined"
    )
  }

  static isTrackEventDto(event: EventDto): event is TrackEventDto {
    return "eventTypeId" in (event as TrackEventDto) && typeof (event as TrackEventDto).eventTypeId !== "undefined"
  }

  static isRemoteConfigEventDto(event: EventDto): event is RemoteConfigEventDto {
    return (
      "parameterId" in (event as RemoteConfigEventDto) &&
      typeof (event as RemoteConfigEventDto).parameterId !== "undefined"
    )
  }

  abstract copyWithUser(user: HackleUser): UserEvent

  toDto(): EventDto {
    return {
      insertId: this.insertId,
      timestamp: this.timestamp,

      userId: this.user.identifiers[IdentifierType.ID],
      identifiers: this.user.identifiers,
      userProperties: PropertyUtil.sanitize(this.user.properties),
      hackleProperties: PropertyUtil.sanitize(this.user.hackleProperties)
    }
  }
}

export class ExposureEvent extends UserEvent {
  experiment: Experiment
  variationId?: VariationId
  variationKey: VariationKey
  decisionReason: DecisionReason
  properties: Properties

  constructor(
    timestamp: Long,
    user: HackleUser,
    experiment: Experiment,
    variationId: VariationId | undefined,
    variationKey: VariationKey,
    decisionReason: DecisionReason,
    properties: Properties,
    insertId?: string
  ) {
    super(timestamp, user, insertId)
    this.experiment = experiment
    this.variationId = variationId
    this.variationKey = variationKey
    this.decisionReason = decisionReason
    this.properties = properties
  }

  copyWithUser(user: HackleUser): ExposureEvent {
    return new ExposureEvent(
      this.timestamp,
      user,
      this.experiment,
      this.variationId,
      this.variationKey,
      this.decisionReason,
      this.properties,
      this.insertId
    )
  }

  toDto(): ExposureEventDto {
    return {
      ...super.toDto(),

      experimentId: this.experiment.id,
      experimentKey: this.experiment.key,
      experimentType: this.experiment.type,
      experimentVersion: this.experiment.version,
      variationId: this.variationId,
      variationKey: this.variationKey,
      decisionReason: this.decisionReason.toString(),
      properties: this.properties
    }
  }
}

export class TrackEvent extends UserEvent {
  eventType: EventType
  event: HackleEvent

  constructor(timestamp: Long, user: HackleUser, eventType: EventType, event: HackleEvent, insertId?: string) {
    super(timestamp, user, insertId)
    this.eventType = eventType
    this.event = event
  }

  copyWithUser(user: HackleUser): TrackEvent {
    return new TrackEvent(this.timestamp, user, this.eventType, this.event, this.insertId)
  }

  toDto(): TrackEventDto {
    return {
      ...super.toDto(),

      eventTypeId: this.eventType.id,
      eventTypeKey: this.eventType.key,
      value: this.event.value || 0,
      properties: PropertyUtil.sanitize(this.event.properties)
    }
  }
}

export class RemoteConfigEvent extends UserEvent {
  parameter: RemoteConfigParameter
  valueId: number | undefined
  decisionReason: DecisionReason
  properties: Properties

  constructor(
    timestamp: Long,
    user: HackleUser,
    parameter: RemoteConfigParameter,
    valueId: number | undefined,
    decisionReason: DecisionReason,
    properties: Properties,
    insertId?: string
  ) {
    super(timestamp, user, insertId)
    this.parameter = parameter
    this.valueId = valueId
    this.decisionReason = decisionReason
    this.properties = properties
  }

  copyWithUser(user: HackleUser): RemoteConfigEvent {
    return new RemoteConfigEvent(
      this.timestamp,
      user,
      this.parameter,
      this.valueId,
      this.decisionReason,
      this.properties,
      this.insertId
    )
  }

  toDto(): RemoteConfigEventDto {
    return {
      ...super.toDto(),
      parameterId: this.parameter.id,
      parameterKey: this.parameter.key,
      parameterType: this.parameter.type,
      valueId: this.valueId,
      decisionReason: this.decisionReason.toString(),
      properties: PropertyUtil.sanitize(this.properties)
    }
  }
}
